// @ts-check

import {
  CloseOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  MoreOutlined,
  PlusCircleOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
  ExportOutlined,
  ImportOutlined
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Divider,
  Dropdown,
  Input,
  InputNumber,
  List,
  Menu,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
  notification,
} from "antd";
import useModal from "antd/lib/modal/useModal";
import axios from "axios";
import moment from "moment";
import { createContext, forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import tr from "../i18n";
import { useDevice } from "./device-context";
import { EventSchedulerProvider } from "./event-scheduler-context";
import { useGarden } from "./garden-context";
import { hasAccess, hasRoles, ROLES } from "../utils/AccessLevels";
import { AccessGate } from "../components/AccessGate";

/** @type {import("../types/global").activeDaysMap} */
const activeDaysMap = [
  { name: tr("Monday_short"), id: 1 },
  { name: tr("Tuesday_short"), id: 2 },
  { name: tr("Wednesday_short"), id: 3 },
  { name: tr("Thursday_short"), id: 4 },
  { name: tr("Friday_short"), id: 5 },
  { name: tr("Saturday_short"), id: 6 },
  { name: tr("Sunday_short"), id: 0 },
];

const EVERY_DAY = {
  id: 8,
  name: tr("Every Day"),
};

/**
 * Returns the day ids in `days` that are NOT enabled on the garden.
 * @param {number[]} days - day ids the template waters on
 * @param {number[]} gardenWateringDays - garden.wateringDays
 * @returns {number[]}
 */
const getPassiveDays = (days, gardenWateringDays) => {
  const active = new Set(gardenWateringDays || []);
  return [...new Set(days)].filter((d) => !active.has(d));
};

/**
 * @param {number[]} dayIds
 * @returns {string}
 */
const formatDayNames = (dayIds) =>
  dayIds
    .map((id) => activeDaysMap.find((d) => d.id === id)?.name)
    .filter(Boolean)
    .join(", ");

/**
* Extracts the set of day ids a template waters on, regardless of type.
* @param {import("../types/global").QuickPlanTemplate} template
* @returns {number[]}
*/
const getTemplateDays = (template) => {
  if (template.type === "custom_multiple") {
    return [...new Set((template.startTimes || []).map((s) => parseInt(s.day, 10)).filter((d) => !isNaN(d)))];
  }
  return template.activeDays || [];
};

const pad = (/** @type {number} */ d) => {
  return d < 10 ? "0" + d.toString() : d.toString();
};

const HOURS = Array.from({ length: 24 }, (_, i) => pad(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad(i));

/**
 * Shows a modal with the results of applying a garden template.
 * @param {Object} params
 * @param {import("../types/global").TemplateDeviceInfo[]} params.appliedDevices - Devices successfully applied from the template.
 * @param {import("../types/global").TemplateDeviceInfo[]} params.missingDevices - Devices included in the template but missing from the garden.
 * @param {import("../types/global").TemplateDeviceInfo[]} params.newDevices - Devices in the garden not included in the template.
 * @param {(list: import("../types/global").TemplateDeviceInfo[]) => string} params.formatDeviceList - Function to format a list of devices into a string.
 */
const showTemplateResultModal = ({ appliedDevices, missingDevices, newDevices, formatDeviceList }) => {
  Modal.info({
    title: <Typography.Title level={4}>{tr("Template applied successfully")}</Typography.Title>,
    content: (
      <>
        {appliedDevices.length > 0 && (
          <Typography.Text strong style={{ display: "block", fontSize: 16, marginBottom: 8 }}>
            {tr("Device plans were updated based on the selected template.")}
            <br />
            {tr("Affected Devices")}: {formatDeviceList(appliedDevices)}
          </Typography.Text>
        )}

        {missingDevices.length > 0 && (
          <Typography.Text strong style={{ display: "block", fontSize: 16, color: "#a8071a", marginBottom: 8 }}>
            {tr("These devices were included in the template but are no longer found in this garden")}: {formatDeviceList(missingDevices)}
          </Typography.Text>
        )}

        {newDevices.length > 0 && (
          <Typography.Text strong style={{ display: "block", fontSize: 16, color: "#d48806", marginBottom: 8 }}>
            {tr("These devices exist in the garden but were not included in the saved template")} {formatDeviceList(newDevices)}
          </Typography.Text>
        )}

        {appliedDevices.length === 0 && missingDevices.length === 0 && newDevices.length === 0 && (
          <Typography.Text style={{ display: "block", fontSize: 16 }}>{tr("No changes were made.")}</Typography.Text>
        )}
      </>
    ),
    okText: tr("Ok"),
    centered: true,
    maskClosable: false,
    width: 600,
  });
};

const PlansContext = /** @type {import("react").Context<import("../types/global").PlansContextValue | undefined>} */ (createContext(undefined));

export function usePlans() {
  const context = /** @type {import("../types/global").PlansContextValue | undefined} */ (useContext(PlansContext));
  if (!context) throw new Error("usePlans must be used within a PlansProvider");
  return context;
}

/**
 * @param {import("react").PropsWithChildren<{}>} props
 * @returns {import("react").ReactElement | null}
 */
export function PlansProvider({ children }) {
  const { gardenId, garden } = useGarden();
  const { devices } = useDevice();
  const queryClient = useQueryClient();
  const [modal, contextHolder] = useModal();
  const [uncommittedChages, setUncommittedChanges] = useState(/** @type {import("../types/global").UncommittedRequest[]} */([]));
  const addQuickPlanRef = useRef(/** @type {{ getValues: import("../types/global").getValueFunction } | null} */(null));
  const addCustomMultipleQuickPlanRef = useRef(/** @type {{ getCustomMultipleValues: import("../types/global").getCustomMultipleValueFunction } | null} */(null));
  const [isQuickPlansVisible, setIsQuickPlansVisible] = useState(false);
  const [gardenTemplates, setGardenTemplates] = useState(/** @type {import("../types/global").GardenTemplate[]} */([]));
  const [gardenPlanTemplatePreview, setGardenPlanTemplatePreview] = useState(
    /** @type {import("../types/global").GardenPlanTemplatePreviewApiResponse | undefined} */(undefined),
  );
  const [isLoadingGardenPlanTemplatePreview, setIsLoadingGardenPlanTemplatePreview] = useState(false);
  const [lastPreviewedTemplateId, setLastPreviewedTemplateId] = useState(/** @type {string | null} */(null));

  const invalidatePlansQueries = useCallback(() => {
    queryClient.invalidateQueries("Garden");
    queryClient.invalidateQueries("GardenDevicePlans");
    queryClient.invalidateQueries("GardenDevicePlansPerDay");
    queryClient.invalidateQueries(["GardenActiveTemplates", gardenId]);
    queryClient.invalidateQueries(["QuickPlanTemplates", gardenId]);
  }, [queryClient, gardenId]);

  const { mutateAsync: commitSchedulesAsync, isLoading: isCommittingSchedules } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<Extract<import("../types/global").CommitSchedulesResponse, {success:true}>>, import("axios").AxiosResponse<Extract<import("../types/global").CommitSchedulesResponse, {success: false}>>, import("../types/global").CommitSchedulesRequest>} */ (
      useMutation(({ commits }) => axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/commit-schedules`, { gardenId, commits, coraFlag: true }), {
        onSuccess: () => invalidatePlansQueries(),
        onError: (err, variables) => {
          console.error("Commit schedules mutation failed:", err);

          // Show error notification in development
          if (process.env.NODE_ENV === "development") {
            console.warn("Commit schedules mutation variables:", variables);
          }
        },
      })
    );

  const { mutateAsync: createQuickPlanTemplate, isLoading: isCreatingQuickPlanTemplate } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").CreateQuickPlanTemplateSuccessResponse>, import("axios").AxiosResponse<import("../types/global").CreateQuickPlanTemplateErrorResponse>, import("../types/global").CreateQuickPlanTemplateRequest>} */ (
      useMutation(
        (request) => {
          return axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/quick-plan-templates`, request);
        },
        {
          onSuccess: () => {
            notification.success({
              message: tr("Quick plan template created"),
              placement: "topRight",
            });

            if (!isQuickPlansVisible) {
              setIsQuickPlansVisible(true);
            }

            invalidatePlansQueries();
            refetchQuickPlanTemplates();
          },
          onError: (err) => {
            const errorMessage = err?.data?.error || err?.data?.message || "Unknown error";

            notification.error({
              message: tr("Failed to create quick plan template"),
              description: errorMessage,
              placement: "topRight",
            });

            console.error("Create quick plan template mutation failed:", err);
          },
        },
      )
    );

  const { mutate: deleteQuickPlanTemplate, isLoading: isDeletingQuickPlanTemplate } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").DeleteQuickPlanTemplateSuccessResponse>, import("axios").AxiosResponse<import("../types/global").DeleteQuickPlanTemplateErrorResponse>, string, unknown>} */ (
      useMutation(
        (templateId) => {
          return axios.delete(`${process.env.REACT_APP_PLANNER_API_END_POINT}/quick-plan-templates/${templateId}`);
        },
        {
          onSuccess: () => {
            notification.success({
              message: tr("Quick plan deleted successfully"),
              placement: "topRight",
            });

            queryClient.invalidateQueries("Garden");
            queryClient.invalidateQueries("GardenDevicePlans");
            queryClient.invalidateQueries("GardenDevicePlansPerDay");
            queryClient.invalidateQueries(["QuickPlanTemplates", gardenId]);
            queryClient.invalidateQueries(["GardenActiveTemplates", gardenId]);
            refetchQuickPlanTemplates();
          },
          onError: (err) => {
            const errorMessage = err?.data?.error || err?.data?.message || "Unknown error";

            notification.error({
              message: tr("Failed to delete quick plan template"),
              description: errorMessage,
              placement: "topRight",
            });

            console.error("Delete quick plan template mutation failed:", err);
          },
        },
      )
    );

  const { mutate: updateActiveTemplates, isLoading: isUpdatingActiveTemplates } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<Extract<import("../types/global").UpdateActiveTemplatesResponse, {success:true}>>, import("axios").AxiosResponse<Extract<import("../types/global").UpdateActiveTemplatesResponse, {success: false}>>, string[]>} */ (
      useMutation(
        (templateIds) => {
          return axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/active-templates`, {
            gardenId,
            templateIds,
          });
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(["QuickPlanTemplates", gardenId]);

            notification.success({
              message: tr("Changes saved successfully"),
              placement: "topRight",
            });

            queryClient.invalidateQueries("Garden");
            queryClient.invalidateQueries("GardenDevicePlans");
            queryClient.invalidateQueries("GardenDevicePlansPerDay");
            queryClient.invalidateQueries(["GardenActiveTemplates", gardenId]);
          },
          onError: () => {
            notification.error({
              message: tr("Something went wrong while saving changes"),
              placement: "topRight",
            });
          },
        },
      )
    );

  const { data: gardenActiveTemplates, isLoading: isLoadingGardenActiveTemplates } =
    /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/global").GardenActiveTemplatesApiResponse>, import("axios").AxiosResponse< import("../types/global").GardenActiveTemplatesErrorResponse>>} */ (
      useQuery(
        ["GardenActiveTemplates", gardenId],
        () => {
          return axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${gardenId}/active-templates`);
        },
        { enabled: !!gardenId, refetchOnWindowFocus: false },
      )
    );

  const {
    data: quickPlanTemplates,
    isLoading: isLoadingQuickPlanTemplates,
    refetch: refetchQuickPlanTemplates,
  } = /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/global").QuickPlanTemplatesApiResponse>, import("axios").AxiosResponse< import("../types/global").QuickPlanTemplatesErrorResponse>>} */ (
      useQuery(
        ["QuickPlanTemplates", gardenId],
        () => {
          return axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/quick-plan-templates?gardenId=${gardenId}`);
        },
        { refetchOnWindowFocus: false },
      )
    );

  // Fetch templates for current garden
  const { isLoading: isLoadingGardenTemplates, refetch: refetchGardenTemplates } =
    /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/global").GardenTemplatesResponse>, import("axios").AxiosResponse<import("../types/global").GardenTemplatesErrorResponse>>} */ (
      useQuery(["GardenTemplates", gardenId], () => axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${gardenId}/templates`), {
        enabled: !!gardenId,
        refetchOnWindowFocus: false,
        onSuccess: (res) => {
          setGardenTemplates(res.data.templates || []);
        },
      })
    );

  // Save garden template
  const onSaveGardenTemplate = useCallback(
    (templateName) => {
      if (!gardenId) return Promise.reject("No gardenId");

      return axios
        .post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${gardenId}/templates`, { templateName })
        .then((res) => {
          notification.success({ message: tr("Template saved"), placement: "topRight" });
          refetchGardenTemplates();
          return res; // Return AxiosResponse so the type matches
        })
        .catch((err) => {
          console.error(err);
          notification.error({ message: tr("Failed to save template"), placement: "topRight" });
          throw err; // Re-throw to maintain Promise<AxiosResponse>
        });
    },
    [gardenId, refetchGardenTemplates],
  );

  // Load garden template
  const onLoadGardenTemplate = useCallback(
    async (templateId) => {
      if (!gardenId || !templateId) return { appliedDevices: [], skippedDevices: [] };

      try {
        const { data } = await axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/templates/${templateId}/apply`);

        /**
         * @type {(list: import("../types/global").TemplateDeviceInfo[]) => string}
         */
        const formatDeviceList = (list) =>
          list
            .map(
              /**
               * @param {import("../types/global").TemplateDeviceInfo} d
               */
              (d) => `${d.deviceName}`,
            )
            .join(", ");

        /** @type {import("../types/global").TemplateDeviceInfo[]} */ const missingTemplateDevices = data.missingTemplateDevices || [];
        /** @type {import("../types/global").TemplateDeviceInfo[]} */ const newGardenDevices = data.newGardenDevices || [];
        /** @type {import("../types/global").TemplateDeviceInfo[]} */ const appliedDevices = data.appliedDevices || [];

        // Show result modal
        showTemplateResultModal({
          appliedDevices,
          missingDevices: missingTemplateDevices,
          newDevices: newGardenDevices,
          formatDeviceList,
        });

        setLastPreviewedTemplateId(null);
        setGardenPlanTemplatePreview(undefined);
        // Single standardized invalidation
        invalidatePlansQueries();
        return {
          appliedDevices,
          skippedDevices: missingTemplateDevices,
        };
      } catch (err) {
        console.error(err);

        notification.error({
          message: tr("Failed to apply template"),
          placement: "topRight",
        });

        return { appliedDevices: [], skippedDevices: [] };
      }
    },
    [gardenId],
  );

  /**
   * Preview a garden template without applying it
   * @param {string} templateId - The ID of the template to preview
   * @returns {Promise<import('axios').AxiosResponse<GardenPlanTemplatePreviewApiResponse>>}
   */
  const previewGardenTemplate = useCallback(
    async (templateId) => {
      if (!templateId) return Promise.reject("No templateId provided");

      try {
        setIsLoadingGardenPlanTemplatePreview(true);

        const response = await axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${gardenId}/template?templateId=${templateId}`);

        setGardenPlanTemplatePreview(response.data);
        setLastPreviewedTemplateId(templateId);
        return response; // returning Axios response to match type
      } catch (err) {
        console.error("Error previewing garden template:", err);
        return Promise.reject(err);
      } finally {
        setIsLoadingGardenPlanTemplatePreview(false);
      }
    },
    [gardenId],
  );

  const { mutate: deleteGardenPlans, isLoading: isDeletingGardenPlans } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").DeleteGardenPlansSuccessResponse>, import("axios").AxiosResponse<import("../types/global").DeleteGardenPlansErrorResponse>, string, unknown>} */ (
      useMutation(
        (gardenId) => {
          return axios.delete(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/plans`, {
            data: {
              gardenId
            },
          });
        },
        {
          onSuccess: () => {
            notification.success({
              message: tr("Garden plans deleted successfully"),
              placement: "topRight",
            });

            queryClient.invalidateQueries("Garden");
            queryClient.invalidateQueries("GardenDevicePlans");
            queryClient.invalidateQueries("GardenDevicePlansPerDay");
            queryClient.invalidateQueries(["QuickPlanTemplates", gardenId]);
            queryClient.invalidateQueries(["GardenActiveTemplates", gardenId]);
            refetchQuickPlanTemplates();
          },
          onError: (err) => {
            const errorMessage = err?.data?.error || err?.data?.message || "Unknown error";

            notification.error({
              message: tr("Failed to delete garden plans template"),
              description: errorMessage,
              placement: "topRight",
            });

            console.error("Delete garden plans failed:", err);
          },
        },
      )
    );

  /**
   *
   * @param {number} duration
   * @returns
   */
  function padDurationWithZerosHelper(duration) {
    return duration < 10 ? "0" + duration.toString() : duration.toString();
  }

  /** @type {import("../types/global").calculateEndTimeFunction} */
  const calculateEndTime = useCallback(
    /**
     * @param {Parameters<import("../types/global").calculateEndTimeFunction>[0]} template
     * @returns {ReturnType<import("../types/global").calculateEndTimeFunction>}
     */
    (template) => {
      let totalDuration = 0;

      if (!devices)
        return {
          endTime: "00:00",
          notInSameDay: false,
          totalDuration: { d: 0, h: 0, m: 0 },
        };

      const { startTime, duration } = template;

      // converting start time to minutes
      let mins = startTime
        .split(":")
        .map((p, i) => (i === 0 ? parseInt(p) * 60 : parseInt(p)))
        .reduce((p, c) => (p += c), 0);

      devices.forEach((d) => {
        if (!d.Device || !d.Device.valves || d.model === "BL") return;

        const valves = JSON.parse(d.Device.valves);
        if (valves === null) return;
        Object.values(valves)
          .filter((v) => v.state === "on")
          .forEach((v) => {
            mins += duration;
            totalDuration += duration;
          });
      });

      let notInSameDay = false;
      let endHours = Math.floor(mins / 60);
      const endMins = Math.floor(mins % 60);

      /**
       * if endHours is greater than 24,
       * it means the end time is in the next day.
       * Mod 24 to get the actual hour
       */
      notInSameDay = endHours > 24;
      endHours = endHours % 24;

      const durationHours = Math.floor(totalDuration / 60);
      const durationMins = Math.floor(totalDuration % 60);

      return {
        endTime: `${padDurationWithZerosHelper(endHours > 24 ? endHours - 24 : endHours)}:${padDurationWithZerosHelper(endMins)}`,
        totalDuration: {
          d: Math.floor(totalDuration / 1440),
          h: durationHours,
          m: durationMins,
        },
        notInSameDay,
      };
    },
    [devices],
  );

  /** @type {import("../types/global").generateTextForNotInSameDayFunction} */
  const generateTextForNotInSameDay = useCallback(
    /**
     *
     * @param {import("../types/global").QuickPlanTemplate} template
     * @returns {string}
     */
    (template) => {
      const { totalDuration } = calculateEndTime(template);
      if (typeof totalDuration !== "object" || !totalDuration || !("d" in totalDuration)) return "";

      const { d: days } = totalDuration;
      if (days === 0) {
        return "Sonraki gün"; //'Next day'
      }

      if (days > 0) {
        return `+${days * 24} hours`;
      }

      return "";
    },
    [calculateEndTime],
  );

  const openQuickPlanUpdateConfirmationModal = useCallback(
    /** @type {import("../types/global").openQuickPlanUpdateConfirmationModalFunction} */
    (newActiveTemplateIds, activatedTemplate) => {
      const passiveDays = activatedTemplate
        ? getPassiveDays(getTemplateDays(activatedTemplate), garden?.wateringDays)
        : [];

      modal.confirm({
        title: tr("Warning"),
        content: (
          <>
            <div>{tr("There are unsaved changes to the watering schedule")}</div>
            {passiveDays.length > 0 && (
              <div style={{ marginTop: 12, color: "#d48806", display: "flex", gap: 8 }}>
                <ExclamationCircleOutlined style={{ marginTop: 3 }} />
                <span>
                  {tr("This plan includes days that are not active on the garden")}: <strong>{formatDayNames(passiveDays)}</strong>.{" "}
                  {tr("Watering will not run on those days until you enable them in the garden's watering days.")}
                </span>
              </div>
            )}
          </>
        ),
        okText: tr("Save"),
        cancelText: tr("Cancel"),
        onOk: () => {
          updateActiveTemplates(newActiveTemplateIds);
        },
      });
    },
    [modal, updateActiveTemplates, garden?.wateringDays],
  );

  const openAddNewQuickPlanTemplateModal = useCallback(
    (gardenId) => {
      modal.confirm({
        cancelText: tr("Cancel"),
        closable: true,
        content: <PlansProvider.AddQuickPlan ref={addQuickPlanRef} />,
        icon: null,
        okText: tr("Save"),
        onOk: () => {
          if (addQuickPlanRef.current) {
            /** @type {import("../types/global").CreateQuickPlanTemplateRequest} */
            const custom = {
              gardenId,
              type: "custom",
              ...addQuickPlanRef.current.getValues(),
            };
            createQuickPlanTemplate(custom);
          }
        },
        title: (
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            {tr("Create quick plan templates")}
          </Typography.Title>
        ),
      });
    },
    [createQuickPlanTemplate, modal],
  );

  const openAddNewCustomMultipleQuickPlanTemplateModal = useCallback(
    (gardenId) => {
      modal.confirm({
        cancelText: tr("Cancel"),
        closable: true,
        content: <PlansProvider.AddCustomMultipleQuickPlan ref={addCustomMultipleQuickPlanRef} />,
        icon: null,
        width: "50%",
        okText: tr("Save"),
        onOk: () => {
          if (addCustomMultipleQuickPlanRef.current) {
            /** @type {import("../types/global").CreateQuickPlanTemplateRequest} */
            const custom = {
              gardenId: gardenId,
              type: "custom_multiple",
              ...addCustomMultipleQuickPlanRef.current.getCustomMultipleValues(),
            };
            createQuickPlanTemplate(custom);
          }
        },
        title: (
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            {tr("Create a custom plan")}
          </Typography.Title>
        ),
      });
    },
    [createQuickPlanTemplate, modal],
  );

  const plansContextValue = useMemo(() => {
    /** @type {import("../types/global").PlansContextValue} */
    const value = {
      calculateEndTime,
      commitSchedulesAsync,
      createQuickPlanTemplate,
      deleteQuickPlanTemplate,
      deleteGardenPlans,
      gardenActiveTemplates: gardenActiveTemplates?.data?.templates || [],
      generateTextForNotInSameDay,
      isCommittingSchedules,
      isCreatingQuickPlanTemplate,
      isDeletingQuickPlanTemplate,
      isDeletingGardenPlans,
      isLoadingQuickPlanTemplates,
      isLoadingGardenActiveTemplates,
      isUpdatingActiveTemplates,
      openAddNewQuickPlanTemplateModal,
      openAddNewCustomMultipleQuickPlanTemplateModal,
      openQuickPlanUpdateConfirmationModal,
      quickPlanTemplates: quickPlanTemplates?.data?.templates || [],
      refetchQuickPlanTemplates,
      uncommittedChangesState: [uncommittedChages, setUncommittedChanges],
      updateActiveTemplates,
      isQuickPlansVisible,
      setIsQuickPlansVisible,
      gardenTemplates,
      isLoadingGardenTemplates,
      refetchGardenTemplates,
      onSaveGardenTemplate,
      onLoadGardenTemplate,
      gardenPlanTemplatePreview: gardenPlanTemplatePreview,
      setGardenPlanTemplatePreview,
      isLoadingGardenPlanTemplatePreview,
      previewGardenTemplate,
      lastPreviewedTemplateId,
      setLastPreviewedTemplateId,
    };

    return value;
  }, [
    calculateEndTime,
    commitSchedulesAsync,
    createQuickPlanTemplate,
    deleteQuickPlanTemplate,
    deleteGardenPlans,
    gardenActiveTemplates?.data?.templates,
    generateTextForNotInSameDay,
    isCommittingSchedules,
    isCreatingQuickPlanTemplate,
    isDeletingQuickPlanTemplate,
    isDeletingGardenPlans,
    isLoadingGardenActiveTemplates,
    isLoadingQuickPlanTemplates,
    isUpdatingActiveTemplates,
    openAddNewQuickPlanTemplateModal,
    openAddNewCustomMultipleQuickPlanTemplateModal,
    openQuickPlanUpdateConfirmationModal,
    quickPlanTemplates?.data?.templates,
    refetchQuickPlanTemplates,
    uncommittedChages,
    updateActiveTemplates,
    isQuickPlansVisible,
    setIsQuickPlansVisible,
    gardenTemplates,
    isLoadingGardenTemplates,
    refetchGardenTemplates,
    onSaveGardenTemplate,
    onLoadGardenTemplate,
    gardenPlanTemplatePreview,
    setGardenPlanTemplatePreview,
    isLoadingGardenPlanTemplatePreview,
    previewGardenTemplate,
    lastPreviewedTemplateId,
    setLastPreviewedTemplateId,
  ]);

  return (
    <PlansContext.Provider value={plansContextValue}>
      {children}
      {contextHolder}
    </PlansContext.Provider>
  );
}

PlansProvider.AddQuickPlan = forwardRef(function PlansProviderAddQuickPlan({ children }, ref) {
  const [timePickerValue, setTimePickerValue] = useState(/** @type {import("moment").Moment |  undefined} */(moment()));
  const [wateringDuration, setWateringDuration] = useState(/** @type {import("antd/lib/statistic/utils").valueType | undefined} */(10));
  const [selectedDays, setSelectedDays] = useState(
    /** @type {() => import("../types/global").Prettify<((typeof activeDaysMap[number]) & { checked: boolean; })>[]} */
    (() => activeDaysMap.map((d) => ({ ...d, checked: true }))),
  );
  const [locale] = useState(localStorage.getItem("locale") || "tr");

  // Determine format for TimePicker
  const timeFormat = locale === "en" ? "hh:mm A" : "HH:mm";

  const getValues = useCallback(
    () => ({
      activeDays: selectedDays
        .filter((d) => d.checked && gardenActiveDays.has(d.id))
        .map((d) => d.id),
      duration: String(wateringDuration || 0),
      startTime: timePickerValue ? timePickerValue.format("HH:mm") : "00:00",
    }),
    [selectedDays, timePickerValue, wateringDuration, gardenActiveDays],
  );
  const { garden } = useGarden();

  const passiveSelectedDays = useMemo(
    () => getPassiveDays(selectedDays.filter((d) => d.checked).map((d) => d.id), garden?.wateringDays),
    [selectedDays, garden?.wateringDays],
  );

  const gardenActiveDays = useMemo(
    () => new Set(garden?.wateringDays || []),
    [garden?.wateringDays],
  );

  useImperativeHandle(ref, () => ({ getValues }), [getValues]);

  return (
    <Row gutter={[0, 12]}>
      <Col span={24}>
        <Typography.Text style={{ display: "inline-block", lineHeight: 1.2 }}>
          {tr("Each zone will be watered for selected number of minutes on selected days")}
        </Typography.Text>
      </Col>

      <Col span={24}>
        <Divider style={{ marginBottom: 4, marginTop: 0 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {tr("Watering Days")}
          </Typography.Text>
        </Divider>

        <Row justify="space-between" wrap={false}>
          {selectedDays.map((selectedDay) => {
            const isGardenDisabled = !gardenActiveDays.has(selectedDay.id);

            return (
              <Col key={selectedDay.id} span={3}>
                <Tooltip title={isGardenDisabled ? tr("This day is not active on the garden") : ""}>
                  <Tag.CheckableTag
                    checked={selectedDay.checked && !isGardenDisabled}
                    onChange={(checked) => {
                      if (isGardenDisabled) return;
                      setSelectedDays((prevSelectedDays) =>
                        prevSelectedDays.map((d) => (d.id === selectedDay.id ? { ...d, checked } : d)),
                      );
                    }}
                    style={{
                      borderColor: "lightgray",
                      borderRadius: 5,
                      borderWidth: 0.5,
                      margin: 0,
                      width: "100%",
                      textAlign: "center",
                      ...(isGardenDisabled
                        ? { opacity: 0.4, cursor: "not-allowed", pointerEvents: "auto" }
                        : {}),
                    }}
                  >
                    {selectedDay.name}
                  </Tag.CheckableTag>
                </Tooltip>
              </Col>
            );
          })}
        </Row>
        {passiveSelectedDays.length > 0 && (
          <Col span={24} style={{ marginTop: 8 }}>
            <Typography.Text type="danger" style={{ fontSize: 12, display: "flex", gap: 6, alignItems: "baseline" }}>
              <ExclamationCircleOutlined />
              <span>
                {tr("Selected days not active on the garden")}: <strong>{formatDayNames(passiveSelectedDays)}</strong>.{" "}
                {tr("Enable them in the garden's watering days for this plan to run on those days.")}
              </span>
            </Typography.Text>
          </Col>
        )}
      </Col>

      <Col span={24}>
        <Row gutter={[10, 0]}>
          <Col span={12}>
            <Divider style={{ marginBottom: 4, marginTop: 0 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {tr("Start at")}
              </Typography.Text>
            </Divider>

            <TimePicker
              format={timeFormat}
              showNow={false}
              use12Hours={locale === "en"}
              onSelect={(value) => setTimePickerValue(value || undefined)}
              value={timePickerValue}
              minuteStep={1}
              style={{ width: "100%" }}
            />
          </Col>

          <Col span={12}>
            <Divider style={{ marginBottom: 4, marginTop: 0 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {tr("Water for")}
              </Typography.Text>
            </Divider>

            <InputNumber addonAfter={tr("Minutes")} defaultValue={wateringDuration} onChange={(value) => setWateringDuration(value || undefined)} />
          </Col>
        </Row>
      </Col>
    </Row>
  );
});

PlansProvider.AddCustomMultipleQuickPlan = forwardRef(function PlansProviderAddCustomMultipleQuickPlan({ children }, ref) {
  /** @type {[number[], import('react').Dispatch<import('react').SetStateAction<number[]>>]} */
  const [multipleTempDurations, setMultipleTempDurations] = useState([0, 0, 0, 0, 0, 0, 0, 0]);

  /** @type {import('../types/plans').StartTime[]} */
  const startTimesInitial = [];

  /** @type {import('../types/plans').StartTime} */
  const tempStartTimeInitial = {
    day: null,
    hour: null,
    minute: null,
  };

  const [startTimes, setStarttimes] = useState(startTimesInitial);
  const [tempStartTime, setTempStartTime] = useState(tempStartTimeInitial);

  const { garden } = useGarden();

  const passiveSelectedDays = useMemo(
    () => getPassiveDays(startTimes.map((s) => parseInt(s.day, 10)).filter((d) => !isNaN(d)), garden?.wateringDays),
    [startTimes, garden?.wateringDays],
  );

  /**
   * Updates a single field of tempStartTime
   *
   * @param {import('../types/plans').TempStartTimeKey} key
   * @returns {(value: string | null) => void}
   */
  const updateTempStartTime = (key) => (value) => setTempStartTime((p) => ({ ...p, [key]: value }));

  /**
   * Adds a new start time to the list of start times if it doesn't already exist.
   *
   * @param {import('../types/plans').StartTime} startTime - The start time to add.
   * @returns {undefined}
   */
  const pushStartTimes = (startTime) => {
    if (!startTime.day || !startTime.hour || !startTime.minute) return;

    const duplicate = startTimes.find((s) => s.day === startTime.day && s.hour === startTime.hour && s.minute === startTime.minute);
    if (duplicate) return;

    setStarttimes((p) => [...p, startTime]);
  };

  const handleAddStartTime = () => {
    if (tempStartTime.day != null) {
      if (parseInt(tempStartTime.day) === EVERY_DAY.id) {
        for (const d of ["1", "2", "3", "4", "5", "6", "0"]) {
          pushStartTimes({
            day: d,
            hour: tempStartTime.hour,
            minute: tempStartTime.minute,
          });
        }
      } else {
        pushStartTimes({
          day: tempStartTime.day,
          hour: tempStartTime.hour,
          minute: tempStartTime.minute,
        });
      }
    }
  };

  const getCustomMultipleValues = useCallback(
    () => ({
      durations: multipleTempDurations,
      startTimes: startTimes.map((s) => ({
        day: s.day,
        hour: s.hour,
        minute: s.minute,
      })),
    }),
    [multipleTempDurations, startTimes],
  );

  useImperativeHandle(ref, () => ({ getCustomMultipleValues }), [getCustomMultipleValues]);

  return (
    <Card
      bordered={false}
      style={{
        background: "#fafafa",
        padding: 16,
      }}
    >
      <Space direction="vertical" size={2}>
        <Typography.Text type="secondary">{tr("Each zone will be watered for selected number of minutes on selected days")}</Typography.Text>
      </Space>

      {/* Inner content */}
      <Card bordered>
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {/* Water for */}
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {tr("Water for")}
            </Typography.Text>

            <Row gutter={[8, 8]}>
              {[0, 1, 2, 3, 4, 5, 6, 7].map((v) => (
                <Col key={v}>
                  <InputNumber
                    min={0}
                    value={multipleTempDurations[v]}
                    addonBefore={<span style={{ fontSize: 12 }}>{v + 1}</span>}
                    style={{ width: 90 }}
                    onChange={(n) => setMultipleTempDurations((p) => p.map((a, i) => (i === v ? n : a)))}
                  />
                </Col>
              ))}
            </Row>
          </Space>

          {/* Existing start times */}
          {startTimes.length > 0 && (
            <div
              style={{
                background: "#fafafa",
                borderRadius: 6,
                padding: 12,
                position: "relative",
              }}
            >
              <Button size="small" type="text" icon={<CloseOutlined />} style={{ position: "absolute", top: 8, right: 8 }} onClick={() => setStarttimes([])} />

              <Space wrap>
                {startTimes.map((s, i) => (
                  <Tag key={i} color="blue">
                    {activeDaysMap.find((d) => d.id === parseInt(s.day))?.name} · {s.hour}:{s.minute}
                  </Tag>
                ))}
              </Space>
            </div>
          )}

          {/* Start at */}
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {tr("Start Time")}
            </Typography.Text>

            <Space wrap align="baseline">
              <Select placeholder={tr("Day")} style={{ width: 120 }} onChange={updateTempStartTime("day")}>
                <Select.Option key={EVERY_DAY.id}>{EVERY_DAY.name}</Select.Option>
                {activeDaysMap.map((d) => (
                  <Select.Option key={d.id}>{d.name}</Select.Option>
                ))}
              </Select>

              <Select placeholder={tr("Hour")} style={{ width: 90 }} onChange={updateTempStartTime("hour")}>
                {HOURS.map((h) => (
                  <Select.Option key={h}>{h}</Select.Option>
                ))}
              </Select>

              <Select placeholder={tr("Minute")} style={{ width: 90 }} onChange={updateTempStartTime("minute")}>
                {MINUTES.map((m) => (
                  <Select.Option key={m}>{m}</Select.Option>
                ))}
              </Select>

              <Button type="primary" onClick={handleAddStartTime}>
                {tr("Add")}
              </Button>
            </Space>
          </Space>
        </Space>
      </Card>
    </Card>
  );
});

/**
 * @returns {import("react").ReactElement | null}
 */
PlansProvider.AddQuickPlanTemplateButton = function PlansProviderAddQuickPlanTemplateButton() {
  const { openAddNewQuickPlanTemplateModal, openAddNewCustomMultipleQuickPlanTemplateModal } = usePlans();
  const { gardenId } = useGarden();

  const isCustomMultipleTemplatesActive = () => {
    const hostname = window.location.hostname;
    const ISTANBUL_HOSTNAMES = ["istanbul.akillisula.com", "istanbul.grungard.ai"];
    if (hostname && ISTANBUL_HOSTNAMES.includes(hostname)) return true;
  };

  if (!gardenId) return null;

  return (
    <Button
      icon={<PlusCircleOutlined />}
      onClick={() => {
        if (isCustomMultipleTemplatesActive()) openAddNewCustomMultipleQuickPlanTemplateModal(gardenId);
        else openAddNewQuickPlanTemplateModal(gardenId);
      }}
      size="middle"
      style={{ alignItems: "baseline", display: "flex" }}
      type="primary"
    >
      {tr("Add Quick Plan")}
    </Button>
  );
};

/**
 * @returns {import("react").ReactElement | null}
 */
PlansProvider.AdvancedPlanTemplates = function PlansProviderEventScheduler() {
  const { garden } = useGarden();
  const { uncommittedChangesState } = usePlans();
  const [uncommittedChages] = uncommittedChangesState;

  if (!garden) return null;

  if (garden.geoJson.properties?.show_scheduler === "false") return null;

  return (
    <Row gutter={[12, 0]} style={{ position: "relative" }}>
      {/* TOUCHING rows below */}
      <Col md={24} xs={24} style={{ marginBottom: 0, paddingBottom: 0 }}>
        <EventSchedulerProvider.ActiveDaysPerDay />
      </Col>

      <Col md={24} xs={24} style={{ marginBottom: 0, paddingBottom: 0 }}>
        <EventSchedulerProvider.SchedulerModeTitle />
      </Col>

      <Col md={24} xs={24} style={{ marginBottom: 0, paddingBottom: 0 }}>
        <EventSchedulerProvider.EventScheduler />
      </Col>
    </Row>
  );
};

PlansProvider.PlansHeader = function PlansProviderPlansHeader() {
  const { garden } = useGarden();

  if (!garden) return null;

  return (
    <Row
      gutter={[
        { md: 12, xs: 12 },
        { md: 10, xs: 10 },
      ]}
    >
      {/* --- TOP ROW: PLANS TITLE --- */}
      <Col span={24}>
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          {tr("Plans")}
        </Typography.Title>
      </Col>

      {/* --- BOTTOM MAIN ROW: QUICK LEFT, GARDEN RIGHT --- */}
      <Col span={24}>
        <Row justify="space-between" align="middle">
          {/* LEFT: Quick Plans */}
          <Col flex="none">
            <PlansProvider.QuickPlanTemplateButtons />
          </Col>

          {/* RIGHT: Garden Plans */}
          <Col flex="none">
            <PlansProvider.GardenPlanTemplates />
          </Col>
        </Row>
      </Col>

      <Row justify="space-between" align="middle">
        <Col flex="none">
          <PlansProvider.QuickPlanTemplates />
        </Col>
      </Row>

      {/* Divider before Advanced */}
      <Col span={24}>
        <Divider style={{ margin: "2px 0" }} />
      </Col>

      {/* --- ADVANCED PLANS IN A NEW FULL-WIDTH ROW --- */}
      <Col span={24}>
        <PlansProvider.AdvancedPlanTemplates />
      </Col>
    </Row>
  );
};

/**
 *
 * @returns {import("react").ReactElement | null}
 */
PlansProvider.QuickPlanTemplates = function PlansProviderQuickPlanTemplates() {
  const {
    calculateEndTime,
    deleteQuickPlanTemplate,
    gardenActiveTemplates,
    generateTextForNotInSameDay,
    isLoadingQuickPlanTemplates,
    isUpdatingActiveTemplates,
    openQuickPlanUpdateConfirmationModal,
    quickPlanTemplates,
    isQuickPlansVisible,
    isCreatingQuickPlanTemplate,
    isDeletingQuickPlanTemplate
  } = usePlans();

  const [locale] = useState(localStorage.getItem("locale") || "tr");
  const isMutating = isCreatingQuickPlanTemplate || isDeletingQuickPlanTemplate || isUpdatingActiveTemplates;
  const isEmpty = !quickPlanTemplates || quickPlanTemplates.length === 0;

  const loaderContainerStyle = (isMutating && isEmpty) ? {
    minHeight: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingLeft: '24px',
    marginBlock: '10px',
  } : {};

  const formatTime = (/** @type {string} */ timeString) => {
    if (!timeString) return "";
    const date = new Date(`1970-01-01T${timeString}`);
    return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: locale === "en" });
  };

  /**
   * @param {import("../types/global").QuickPlanTemplate} quickPlanTemplate
   * @returns {boolean}
   */
  function isActive(quickPlanTemplate) {
    return gardenActiveTemplates.some((g) => g._id === quickPlanTemplate._id);
  }

  if (process.env.NODE_ENV === "development") {
    window.quickPlanTemplates = quickPlanTemplates;
  }

  return (
    <div style={loaderContainerStyle}>
      <Spin spinning={isMutating} tip={tr("Loading")} size="large">
        {/* Do not show list while loading */}
        {!isLoadingQuickPlanTemplates && isQuickPlansVisible && (
          <List
            dataSource={quickPlanTemplates}
            locale={{
              emptyText: <Space align="center" direction="vertical" size={[0, 0]}></Space>,
            }}
            grid={{
              gutter: 12,
            }}
            renderItem={(quickPlanTemplate) => {
              if (quickPlanTemplate.type === "custom") {
                const { endTime, notInSameDay, totalDuration } = calculateEndTime(quickPlanTemplate);
                return (
                  <List.Item>
                    <Card
                      size="small"
                      style={{
                        borderColor: quickPlanTemplate.status === "active" ? "darkcyan" : "gainsboro",
                        borderRadius: 12,
                        maxWidth: locale === "en" ? "75%" : "100%",
                      }}
                      title={
                        <Row justify="space-between" align="middle">
                          <Col>
                            <Typography.Text strong>{tr("Plan")}</Typography.Text>
                          </Col>
                          <Col>
                            <Space>
                              <Switch
                                checked={isActive(quickPlanTemplate)}
                                checkedChildren={tr("Active")}
                                unCheckedChildren={tr("Passive")}
                                loading={isUpdatingActiveTemplates}
                                onChange={(checked) => {
                                  const templateIds = quickPlanTemplates.filter((t) => t.status !== "disabled").map((t) => t._id);

                                  const payload = checked ? [...templateIds, quickPlanTemplate._id] : templateIds.filter((id) => id !== quickPlanTemplate._id);

                                  openQuickPlanUpdateConfirmationModal(payload, checked ? quickPlanTemplate : undefined);
                                }}
                              />

                              <Dropdown
                                trigger={["click"]}
                                overlay={
                                  <Menu>
                                    <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => deleteQuickPlanTemplate(quickPlanTemplate._id)}>
                                      {tr("Delete Quick Plan")}
                                    </Menu.Item>
                                  </Menu>
                                }
                              >
                                <Button icon={<MoreOutlined />} size="small" style={{ borderRadius: 12 }} />
                              </Dropdown>
                            </Space>
                          </Col>
                        </Row>
                      }
                    >
                      {/* Times & Durations */}
                      <Row gutter={[2, 2]}>
                        <Col span={12}>
                          <Space direction="vertical" size={0}>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {tr("Start")}
                            </Typography.Text>

                            <Typography.Title level={4} style={{ margin: 0 }}>
                              {formatTime(quickPlanTemplate.startTime)}
                            </Typography.Title>
                          </Space>
                        </Col>

                        <Col span={12}>
                          <Space direction="vertical" size={0}>
                            <Space size={4}>
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {tr("End")}
                              </Typography.Text>

                              {notInSameDay && (
                                <Typography.Text
                                  style={{
                                    color: "#0093a7",
                                    fontSize: 12,
                                    textWrap: "nowrap",
                                  }}
                                >
                                  ({generateTextForNotInSameDay(quickPlanTemplate)})
                                </Typography.Text>
                              )}
                            </Space>

                            <Typography.Title level={4} style={{ margin: 0 }}>
                              {formatTime(endTime)}
                            </Typography.Title>
                          </Space>
                        </Col>

                        <Col span={12}>
                          <Space direction="vertical" size={0}>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {tr("Zone Duration")}
                            </Typography.Text>

                            <Typography.Title level={4} style={{ margin: 0 }}>
                              {quickPlanTemplate.duration} {tr("Mins")}
                            </Typography.Title>
                          </Space>
                        </Col>

                        <Col span={12}>
                          <Space direction="vertical" size={0}>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {tr("Total Duration")}
                            </Typography.Text>

                            <Typography.Title level={4} style={{ margin: 0 }}>
                              {totalDuration.h > 0 && `${totalDuration.h}h `}
                              {totalDuration.m > 0 && `${totalDuration.m} `}
                              {tr("Mins")}
                            </Typography.Title>
                          </Space>
                        </Col>
                      </Row>

                      {/* Watering days */}
                      <Divider style={{ marginBlock: 6 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {tr("Watering Days")}
                        </Typography.Text>
                      </Divider>

                      <Row justify="space-between" wrap={false}>
                        {activeDaysMap.map((day) => (
                          <Col key={day.id} span={3.5}>
                            <Tag
                              color={quickPlanTemplate.activeDays?.includes(day.id) ? "#0093a7" : "darkgray"}
                              style={{
                                borderRadius: 5,
                                margin: 0,
                                padding: "0 5px",
                              }}
                            >
                              {day.name}
                            </Tag>
                          </Col>
                        ))}
                      </Row>
                    </Card>
                  </List.Item>
                );
              } else if (quickPlanTemplate.type === "custom_multiple") {
                return (
                  <List.Item>
                    <Card
                      size="small"
                      style={{
                        borderColor: quickPlanTemplate.status === "active" ? "darkcyan" : "gainsboro",
                        borderRadius: 12,
                      }}
                      title={
                        <Row justify="space-between" align="middle">
                          <Col>
                            <Typography.Text strong>{tr("Custom Plan")}</Typography.Text>
                          </Col>

                          <Col>
                            <Space>
                              <Switch
                                checked={isActive(quickPlanTemplate)}
                                checkedChildren={tr("Active")}
                                unCheckedChildren={tr("Passive")}
                                loading={isUpdatingActiveTemplates}
                                onChange={(checked) => {
                                  const templateIds = quickPlanTemplates.filter((t) => t.status !== "disabled").map((t) => t._id);

                                  const payload = checked ? [...templateIds, quickPlanTemplate._id] : templateIds.filter((id) => id !== quickPlanTemplate._id);

                                  openQuickPlanUpdateConfirmationModal(payload, checked ? quickPlanTemplate : undefined);
                                }}
                              />

                              <Dropdown
                                trigger={["click"]}
                                overlay={
                                  <Menu>
                                    <Menu.Item key="delete" icon={<DeleteOutlined />} danger onClick={() => deleteQuickPlanTemplate(quickPlanTemplate._id)}>
                                      {tr("Delete Quick Plan")}
                                    </Menu.Item>
                                  </Menu>
                                }
                              >
                                <Button icon={<MoreOutlined />} size="small" style={{ borderRadius: 12 }} />
                              </Dropdown>
                            </Space>
                          </Col>
                        </Row>
                      }
                    >
                      {/* Zone durations */}
                      <Space direction="vertical" size={6}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {tr("Zone Durations")}
                        </Typography.Text>

                        <Space wrap size={[4, 4]}>
                          {quickPlanTemplate.durations.map((d, i) => (
                            <Tag key={i} color="blue">
                              {i + 1}: {d} {tr("Mins")}
                            </Tag>
                          ))}
                        </Space>
                      </Space>

                      {/* Start times */}
                      <Divider style={{ marginBlock: 6 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {tr("Start Times")}
                        </Typography.Text>
                      </Divider>

                      <Row style={{ marginInlineStart: 10 }} gutter={[8, 8]} wrap={false}>
                        {activeDaysMap.map((day) => {
                          const times = quickPlanTemplate.startTimes
                            ?.filter((s) => parseInt(s.day) === day.id)
                            .sort((a, b) => parseInt(a.hour) * 60 + parseInt(a.minute) - (parseInt(b.hour) * 60 + parseInt(b.minute)));

                          return (
                            <Col
                              key={day.id}
                              flex="1 1 0"
                              style={{
                                minWidth: 80,
                              }}
                            >
                              <Space direction="vertical" size={2}>
                                <Typography.Text
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: times.length ? "#0093a7" : "#999",
                                    textAlign: "center",
                                  }}
                                >
                                  {day.name}
                                </Typography.Text>

                                <Space direction="vertical" size={0} align="center">
                                  {times.length > 0 ? (
                                    times.map((t, i) => (
                                      <Typography.Text key={i} style={{ fontSize: 11 }}>
                                        {t.hour}:{t.minute}
                                      </Typography.Text>
                                    ))
                                  ) : (
                                    <Typography.Text style={{ fontSize: 11, color: "#ccc" }}>—</Typography.Text>
                                  )}
                                </Space>
                              </Space>
                            </Col>
                          );
                        })}
                      </Row>
                    </Card>
                  </List.Item>
                );
              }
            }}
          />
        )}
      </Spin>
    </div>
  );
};

PlansProvider.QuickPlanTemplateButtons = function PlansProviderQuickPlanTemplateButtons() {
  const { quickPlanTemplates, isQuickPlansVisible, setIsQuickPlansVisible } = usePlans();

  return (
    <>
      {/* Always visible toggle button */}
      <Row gutter={[16, 16]} style={{ display: "flex", justifyContent: "start", marginBottom: 16 }}>
        <Space>
          <PlansProvider.AddQuickPlanTemplateButton />
        </Space>
        {quickPlanTemplates && quickPlanTemplates.length > 0 && (
          <Button block type="text" style={{ width: "10%" }} onClick={() => setIsQuickPlansVisible(!isQuickPlansVisible)}>
            {isQuickPlansVisible ? tr("Hide Quick Plans") : tr("Show Quick Plans")}
          </Button>
        )}
      </Row>
    </>
  );
};

PlansProvider.GardenPlanTemplates = function PlansProviderGardenPlanTemplates() {
  const {
    gardenTemplates,
    isLoadingGardenTemplates,
    deleteGardenPlans,
    isDeletingGardenPlans,
    onSaveGardenTemplate,
    onLoadGardenTemplate,
    previewGardenTemplate,
    setGardenPlanTemplatePreview,
    lastPreviewedTemplateId,
    setLastPreviewedTemplateId,
    uncommittedChangesState,
  } = usePlans();

  const { gardenId } = useGarden();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPlanDeleteModalVisible, setIsPlanDeleteModalVisible] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [uncommittedChages] = uncommittedChangesState;
  const queryClient = useQueryClient();

  if (!gardenTemplates) return null;

  const DELETE_KEYWORD = tr("DELETE");
  const isDeleteConfirmed = deleteConfirmText.trim().toUpperCase() === DELETE_KEYWORD;


  const openPlanDeleteModal = () => {
    setDeleteConfirmText("");
    setIsPlanDeleteModalVisible(true);
  };

  const closePlanDeleteModal = () => {
    if (isDeletingGardenPlans) return; // don't allow closing mid-request
    setDeleteConfirmText("");
    setIsPlanDeleteModalVisible(false);
  };

  const handleDeleteAllPlans = () => {
    if (!isDeleteConfirmed || !gardenId) return;
    deleteGardenPlans(gardenId, {
      // merged with the provider's onSuccess; both run
      onSuccess: () => {
        setDeleteConfirmText("");
        setIsPlanDeleteModalVisible(false);
      },
    });
  };

  const openModal = () => {
    if (uncommittedChages?.length > 0) {
      // Warn the user about uncommitted changes
      Modal.warning({
        title: tr("Uncommitted Changes"),
        content: tr("You have uncommitted changes. Saving a new template may overwrite these changes."),
        centered: true,
        okText: tr("Ok"),
      });
      return;
    }
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setTemplateName("");
    setIsModalVisible(false);
  };

  const handleSave = () => {
    if (!templateName.trim()) return; // optionally show a warning or disable button
    onSaveGardenTemplate?.(templateName.trim());
    closeModal();
  };

  const confirmLoadTemplate = (templateId /** @type {String} */) => {
    Modal.confirm({
      title: (
        <span
          style={{
            fontSize: 20,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <ExclamationCircleOutlined style={{ color: "red", fontSize: 28 }} />
          {tr("Load Template")}
        </span>
      ),
      content: (
        <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.5 }}>
          {tr("Are you absolutely sure you want to load this template? This action will overwrite all current plans!")}
        </div>
      ),
      okText: tr("Confirm"),
      cancelText: tr("Cancel"),
      centered: true,
      maskClosable: false,
      okButtonProps: {
        danger: true, // makes the confirm button red
        style: { fontWeight: 700, fontSize: 16 },
      },
      icon: null, // remove default icon
      onOk: () => onLoadGardenTemplate?.(templateId),
    });
  };

  const loadMenu = (
    <Menu>
      {gardenTemplates.length > 0 ? (
        gardenTemplates.map((tpl) => (
          <Menu.Item key={tpl._id} onClick={() => previewGardenTemplate?.(tpl._id)}>
            {tpl.templateName}
          </Menu.Item>
        ))
      ) : (
        <Menu.Item disabled>{tr("No saved templates")}</Menu.Item>
      )}
    </Menu>
  );

  return (
    <>
      <Row gutter={12} align="middle" style={{ marginBottom: 12 }}>
        {!!uncommittedChages.length && (
          <Col flex="0 0 auto">
            <EventSchedulerProvider.UncommittedChangesAlert />
          </Col>
        )}
        {/* Save Button */}
        <Col>
          <Tooltip title={tr("Save Garden Template")}>
            <Button type="primary" icon={<ExportOutlined />} loading={isLoadingGardenTemplates} onClick={openModal} />
          </Tooltip>
        </Col>

        {/* Load Button */}
        <Col>
          <Dropdown
            trigger={["click"]}
            overlay={loadMenu}
            onVisibleChange={(visible) => {
              if (visible) {
                // Refetch garden templates when dropdown is opened
                queryClient.invalidateQueries("GardenTemplates");
              }
            }}
          >
            <Tooltip title={tr("Load Template")}>
              <Button icon={<ImportOutlined />} />
            </Tooltip>
          </Dropdown>
        </Col>

        {/* Preview Mode Buttons */}
        {lastPreviewedTemplateId && (
          <>
            <Col>
              <Button type="primary" onClick={() => confirmLoadTemplate(lastPreviewedTemplateId)}>
                {tr("Apply Template")}
              </Button>
            </Col>

            <Col>
              <Button
                onClick={() => {
                  setLastPreviewedTemplateId(null);
                  setGardenPlanTemplatePreview(undefined);
                  queryClient.invalidateQueries("GardenDevicePlans");
                  queryClient.invalidateQueries("GardenDevicePlansPerDay");
                }}
              >
                {tr("Cancel Preview")}
              </Button>
            </Col>
          </>
        )}
        {lastPreviewedTemplateId === null && (
          <AccessGate role={ROLES.TECHNICAL_ADMIN}>
            <Col>
              <Tooltip title={tr("Delete All Plans")}>
                <Button type="danger" icon={<DeleteOutlined />} onClick={openPlanDeleteModal} />
              </Tooltip>
            </Col>
          </AccessGate>
        )
        }
      </Row>

      {/* Save Template Modal */}
      <Modal
        title={tr("Enter Template Name")}
        visible={isModalVisible}
        onOk={handleSave}
        onCancel={closeModal}
        okText={tr("Save")}
        cancelText={tr("Cancel")}
        centered
        maskClosable={false} // Makes modal more dominant
      >
        <Input placeholder={tr("Template Name")} value={templateName} onChange={(e) => setTemplateName(e.target.value)} onPressEnter={handleSave} />
      </Modal>

      {/* Delete All Plans Modal */}
      <Modal
        visible={isPlanDeleteModalVisible}
        onCancel={closePlanDeleteModal}
        centered
        maskClosable={false}
        closable={!isDeletingGardenPlans}
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 20, fontWeight: 700, color: "#cf1322" }}>
            <ExclamationCircleOutlined style={{ color: "#cf1322", fontSize: 26 }} />
            {tr("Delete All Plans")}
          </span>
        }
        footer={[
          <Button key="cancel" onClick={closePlanDeleteModal} disabled={isDeletingGardenPlans}>
            {tr("Cancel")}
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            icon={<DeleteOutlined />}
            loading={isDeletingGardenPlans}
            disabled={!isDeleteConfirmed}
            onClick={handleDeleteAllPlans}
            style={{ fontWeight: 700 }}
          >
            {tr("Delete All Plans")}
          </Button>,
        ]}
      >
        <div
          style={{
            background: "#fff1f0",
            border: "1px solid #ffa39e",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.6 }}>
            {tr("This will permanently delete ALL plans on ALL devices in this garden.")}
          </div>
          <ul style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 14, lineHeight: 1.6 }}>
            <li>{tr("All device plans will be removed and devices will be updated immediately.")}</li>
            <li>{tr("Active templates will be deactivated.")}</li>
            <li>{tr("This operation is IRREVERSIBLE.")}</li>
          </ul>
        </div>

        <div style={{ fontSize: 14, marginBottom: 8 }}>
          {tr("Type {keyword} to confirm:").split("{keyword}")[0]}
          <strong style={{ color: "#cf1322" }}>{DELETE_KEYWORD}</strong>
          {tr("Type {keyword} to confirm:").split("{keyword}")[1]}
        </div>
        <Input
          placeholder={DELETE_KEYWORD}
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          onPressEnter={handleDeleteAllPlans}
          disabled={isDeletingGardenPlans}
          autoFocus
          status={deleteConfirmText && !isDeleteConfirmed ? "error" : undefined}
        />
      </Modal>
    </>
  );
};
